/**
 * Examination / session result editor for an order
 */
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  useDeleteOrderResultAttachmentMutation,
  useGetOrderResultQuery,
  useSaveOrderResultMutation,
  useUploadOrderResultAttachmentMutation,
} from '../../store/api/orderResultsApi';
import { PermissionGuard } from '../common';
import { usePermissions } from '../../hooks/usePermissions';
import { parseApiError } from '../../utils/apiError';

interface OrderResultsPanelProps {
  orderId: number;
  compact?: boolean;
}

const isImageFile = (url: string, name?: string): boolean => {
  const target = (name || url).toLowerCase();
  return /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i.test(target);
};

const OrderResultsPanel = ({ orderId, compact = false }: OrderResultsPanelProps) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('edit_orders');
  const { data, isLoading, refetch } = useGetOrderResultQuery(orderId);
  const [saveResult, { isLoading: isSaving }] = useSaveOrderResultMutation();
  const [uploadAttachment, { isLoading: isUploading }] = useUploadOrderResultAttachmentMutation();
  const [deleteAttachment, { isLoading: isDeleting }] = useDeleteOrderResultAttachmentMutation();

  const [description, setDescription] = useState('');
  const [uploadProgress, setUploadProgress] = useState('');

  useEffect(() => {
    setDescription(data?.data?.description || '');
  }, [data?.data?.description]);

  const attachments = data?.data?.attachments || [];

  const handleSave = async () => {
    try {
      await saveResult({ orderId, description }).unwrap();
      toast.success(t('components.orderResults.toast.saved'));
      refetch();
    } catch (err: unknown) {
      toast.error(parseApiError(err, t('components.orderResults.toast.saveFailed')));
    }
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    let uploaded = 0;
    let failed = 0;

    for (const file of files) {
      setUploadProgress(
        t('components.orderResults.uploadingProgress', {
          current: uploaded + failed + 1,
          total: files.length,
        })
      );
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name);

      try {
        await uploadAttachment({ orderId, formData }).unwrap();
        uploaded += 1;
      } catch {
        failed += 1;
      }
    }

    setUploadProgress('');
    event.target.value = '';

    if (uploaded > 0) {
      toast.success(
        uploaded === 1
          ? t('components.orderResults.toast.fileUploaded')
          : t('components.orderResults.toast.filesUploaded', { count: uploaded })
      );
      refetch();
    }
    if (failed > 0) {
      toast.error(
        failed === 1
          ? t('components.orderResults.toast.uploadFailedOne')
          : t('components.orderResults.toast.uploadFailedMany', { count: failed })
      );
    }
  };

  const handleDeleteAttachment = async (attachmentId: number) => {
    try {
      await deleteAttachment({ orderId, attachmentId }).unwrap();
      toast.success(t('components.orderResults.toast.attachmentDeleted'));
      refetch();
    } catch (err: unknown) {
      toast.error(parseApiError(err, t('components.orderResults.toast.deleteFailed')));
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('components.orderResults.title')}
        </Typography>
        {!compact && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('components.orderResults.subtitle')}
          </Typography>
        )}

        {canEdit ? (
          <>
            <TextField
              fullWidth
              multiline
              minRows={compact ? 5 : 8}
              label={t('components.orderResults.form.description')}
              placeholder={t('components.orderResults.form.descriptionPlaceholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              helperText={t('components.orderResults.form.descriptionHelper')}
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving
                  ? t('components.orderResults.buttons.saving')
                  : t('components.orderResults.buttons.saveDescription')}
              </Button>
              <Button
                variant="outlined"
                component="label"
                startIcon={<AttachFileIcon />}
                disabled={isUploading}
              >
                {isUploading
                  ? t('components.orderResults.buttons.uploading')
                  : t('components.orderResults.buttons.uploadFiles')}
                <input
                  hidden
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                  onChange={handleUpload}
                />
              </Button>
              {uploadProgress ? (
                <Typography variant="body2" color="text.secondary">
                  {uploadProgress}
                </Typography>
              ) : null}
            </Box>
          </>
        ) : description ? (
          <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
            {description}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('components.orderResults.empty.noDescription')}
          </Typography>
        )}

        {attachments.length > 0 ? (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              {t('components.orderResults.uploadedFiles', { count: attachments.length })}
            </Typography>
            <Grid container spacing={2}>
              {attachments.map((attachment) => {
                const fileUrl = attachment.file_url || attachment.file;
                const showImage = fileUrl && isImageFile(fileUrl, attachment.name);

                return (
                  <Grid size={{ xs: 12, sm: 6, md: compact ? 12 : 4 }} key={attachment.id}>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 1.5,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                      }}
                    >
                      {showImage ? (
                        <Box
                          component="img"
                          src={fileUrl}
                          alt={attachment.name || t('components.orderResults.resultImageAlt')}
                          sx={{
                            width: '100%',
                            maxHeight: 180,
                            objectFit: 'cover',
                            borderRadius: 1,
                            bgcolor: 'action.hover',
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: 100,
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                          }}
                        >
                          <ImageIcon color="action" />
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Link
                            href={fileUrl}
                            target="_blank"
                            rel="noopener"
                            underline="hover"
                            sx={{ wordBreak: 'break-word', display: 'block' }}
                          >
                            {attachment.name || t('components.orderResults.attachmentFallback')}
                          </Link>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {new Date(attachment.created_at).toLocaleString()}
                          </Typography>
                        </Box>
                        <PermissionGuard permissions={['edit_orders']}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteAttachment(attachment.id)}
                            disabled={isDeleting}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </PermissionGuard>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('components.orderResults.empty.noFiles')}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default OrderResultsPanel;
